import React, { useState, useEffect, useRef } from "react";
import { Box, Flex, Heading, Text } from "@theme-ui/components";
import { keyframes } from "@emotion/react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { colors, typography } from "../../styles/theme";
import { Button, Icon } from "../atoms";

const openPrimaryCta = (url) => {
    if (url?.includes("#")) {
        window.location.assign(url);
        return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
};

const PersonaSection = ({ personas, heading, description, variant, pageName, regionCode, customStyles, imageSectionCustomStyles, imageCustomStyles, contentSectionWidth, enableStatsLayout }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const tabsContainerRef = useRef(null);
    const [isProgressing, setIsProgressing] = useState(false);
    const [isSelectedTab, setIsSelectedTab] = useState(false);
    const [showArrows, setShowArrows] = useState(false);
    const progressAnimation = keyframes`
    from { width: 0%; }
    to { width: 100%; }
  `;

    const scrollToSelectedTab = () => {
        const tabElement = tabsContainerRef.current?.children[selectedTab];
        if (tabElement) {
            const tabWidth = tabElement.offsetWidth;
            const containerWidth = tabsContainerRef.current.offsetWidth;
            const offset = (containerWidth - tabWidth) / 2;

            tabsContainerRef.current.scrollTo({
                left: tabElement.offsetLeft - offset,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        scrollToSelectedTab();
    }, [selectedTab]);

    const handleLeftArrowClick = () => {
        if (tabsContainerRef.current) {
            const scrollLeft = tabsContainerRef.current.scrollLeft;
            const tabWidth = tabsContainerRef.current.children[0].offsetWidth;
            tabsContainerRef.current.scrollTo({
                left: scrollLeft - tabWidth,
                behavior: 'smooth',
            });

            setSelectedTab((prev) => (prev > 0 ? prev - 1 : prev));
        }
    };

    const handleRightArrowClick = () => {
        if (tabsContainerRef.current) {
            const scrollLeft = tabsContainerRef.current.scrollLeft;
            const tabWidth = tabsContainerRef.current.children[0].offsetWidth;
            tabsContainerRef.current.scrollTo({
                left: scrollLeft + tabWidth,
                behavior: 'smooth',
            });

            setSelectedTab((prev) =>
                prev < personas?.length - 1 ? prev + 1 : prev
            );
        }
    };

    useEffect(() => {
        let timer;
        if (isProgressing) {
            timer = setTimeout(() => {
                setIsProgressing(false);
                setSelectedTab((prevTab) => (prevTab + 1) % personas?.length);
                setIsSelectedTab(false);
            }, isSelectedTab ? 55000 : 20000);
            return () => clearTimeout(timer);
        }
    }, [isProgressing, selectedTab, isSelectedTab, personas?.length]);

    useEffect(() => {
        setIsProgressing(true);
    }, [selectedTab]);

    const handleTabClick = (tabIndex) => {
        setSelectedTab(tabIndex);
        setIsSelectedTab(true);
        setIsProgressing(false);
    };

    useEffect(() => {
        const checkTabWidth = () => {
            const containerWidth = tabsContainerRef.current?.offsetWidth || 0;
            const windowWidth = window.innerWidth;
            if (windowWidth > 1024) {
                setShowArrows(containerWidth > 1056);
            } else {
                setShowArrows(true);
            }
        };
        if (tabsContainerRef.current) {
            checkTabWidth();
        }
        window.addEventListener('resize', checkTabWidth);
        return () => window.removeEventListener('resize', checkTabWidth);
    }, []);
    const hasImageAndTitle = personas?.some(
      persona => persona.image?.gatsbyImageData && persona.title
    );
    const arrowStyles = {
        bottom: hasImageAndTitle ? "2%" : "7%",
        height: hasImageAndTitle ? "10rem" : "5rem",
    };

    if (!variant || variant === "variant1") {
        return (
            <Box sx={{ mx: ["1rem", "1rem", "2rem", "auto"], px: "1rem", ...customStyles }}>
                {heading && ( <Heading sx={{ ...typography.h2, textAlign: "center", p: { m: 0 }, mb: "1.5rem" }} dangerouslySetInnerHTML={{ __html: heading }} />)}
                {description && ( <Text sx={{ ...typography.textMd400, textAlign: "center", p: { m: 0 } }} dangerouslySetInnerHTML={{ __html: description }} />)}
                {personas?.some(
                    persona => persona?.title || persona?.image?.gatsbyImageData
                ) && (
                    <Flex sx={mainTabContainerStyles}>
                        {showArrows && (
                          <>
                            <Flex sx={{ ...tabLeftContainer, ...arrowStyles }} onClick={handleLeftArrowClick}>
                             <Icon name="ChevronLeft" size={32} color={colors.primaryMain} />
                            </Flex>
                            <Flex sx={{ ...tabRightContainer, ...arrowStyles }} onClick={handleRightArrowClick}>
                             <Icon name="ChevronRight" size={32} color={colors.primaryMain} />
                            </Flex>
                          </>
                        )}
                        <Flex
                            ref={tabsContainerRef}
                            sx={tabContainerStyles}
                        >
                            {personas?.map((persona, index) => (
                                <Box
                                    key={persona.title}
                                    sx={{
                                        ...personaTabStyles,
                                        ...(persona.image?.gatsbyImageData
                                            ? { width: '9.063rem' }
                                            : { minWidth: '9.063rem', px: '1rem' }),
                                        ...(persona.image?.gatsbyImageData && !persona.title && { width: 'auto', px: '1rem' }),
                                        border: `2px solid ${selectedTab === index ? colors.primaryMain : colors.neutral200}`,
                                    }}
                                    onClick={() => handleTabClick(index)}
                                >
                                    {selectedTab === index && (
                                        <Box sx={{ ...selectedTabStyles, animation: `${progressAnimation} ${isSelectedTab ? '55s' : '20s'} linear infinite` }} /> 
                                    )}
                                    {persona.image?.gatsbyImageData && (
                                        <GatsbyImage
                                            image={getImage(persona.image.gatsbyImageData)}
                                            alt={persona.image.alt}
                                            sx={imageStylesV1}
                                        />
                                    )}
                                    {persona.title && (
                                        <Heading className="persona-title" sx={{ ...typography.h6, ...(persona.image?.gatsbyImageData && { mt: '1rem' }), position: 'relative', zIndex: 0, color: selectedTab === index ? colors.primary700 : colors.neutral900, }}>
                                            {persona.title}
                                        </Heading>
                                    )}
                                </Box>
                            ))}
                        </Flex>
                    </Flex>
                )}
                {selectedTab !== null && (
                    <Box sx={{ mt: "2rem" }}>
                        <Flex sx={desktopSectionStylesV1}>
                            <Flex sx={{ flex: "1", flexDirection: "column" }}>
                                {personas[selectedTab]?.sections?.slice(0, 2).map((section, index) => (
                                    <Flex
                                        key={`desktop-section-${index}`}
                                        sx={{
                                            mb: index === 0 ? "1.5rem" : "0",
                                            backgroundColor: colors[`${section?.color}100`],
                                            ...(index === 0 && { border: `1px solid ${colors.neutral200}` }),
                                            ...featureDescriptionV1,
                                        }}
                                    >
                                        <Heading
                                            sx={{ ...typography.h5, mt: "1.5rem", p: { m: 0 }, color: colors.neutral900 }}
                                            dangerouslySetInnerHTML={{ __html: section?.heading }}
                                        />
                                        {index === 1 && enableStatsLayout ? ( 
                                            <Box
                                                sx={enableStatsLayoutV1}
                                                dangerouslySetInnerHTML={{
                                                    __html: section?.description,
                                                }}
                                            />
                                            ) : (
                                            <Box
                                                sx={listStyles(section?.bulletColor)}
                                                dangerouslySetInnerHTML={{ __html: section?.description }}
                                            />
                                        )}
                                    </Flex>
                                ))}
                            </Flex>
                            {personas[selectedTab]?.sections[2] && (
                                <Flex
                                    sx={{
                                        ...featureDescriptionV1,
                                        backgroundColor: colors[`${personas[selectedTab]?.sections[2]?.color}100`],
                                        mt: ["1.5rem", "1.5rem", "1.5rem", 0],
                                        ...imageSectionCustomStyles,
                                    }}
                                >
                                    {personas[selectedTab]?.sections[2]?.image && (
                                        <Box sx={{ pt: "2rem", ...imageCustomStyles }}> 
                                            <GatsbyImage
                                                image={getImage(personas[selectedTab]?.sections[2].image.gatsbyImageData)}
                                                alt={personas[selectedTab]?.sections[2].heading}
                                                style={{ width: "100%", height: "auto" }}
                                            />
                                        </Box>
                                    )}
                                </Flex>
                            )}
                        </Flex>
                    </Box>
                )}
                <Flex sx={{ justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                    {selectedTab !== null && personas[selectedTab] && (
                        (personas[selectedTab]?.primaryCtaName || personas[selectedTab]?.secondaryCtaName) && (
                            <Flex sx={ctaContainerStyles}>
                                {personas[selectedTab]?.primaryCtaName && personas[selectedTab]?.primaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="filled"
                                        id={`${pageName.toLowerCase()}_persona_variant1_primary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() => openPrimaryCta(personas[selectedTab].primaryCtaUrl)}
                                        customStyles={{ width: "max-content" }}
                                    >
                                        {personas[selectedTab]?.primaryCtaName}
                                    </Button>
                                )}
                                {personas[selectedTab]?.secondaryCtaName && personas[selectedTab]?.secondaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="outlined"
                                        id={`${pageName.toLowerCase()}_persona_variant1_secondary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() =>
                                            window.open( personas[selectedTab]?.secondaryCtaUrl, "_blank", "noopener,noreferrer" )
                                        }
                                        customStyles={{ width: "max-content" }}
                                    >
                                        {personas[selectedTab]?.secondaryCtaName}
                                    </Button>
                                )}
                            </Flex>
                        )
                    )}
                </Flex>
            </Box>
        );
    } else if (variant === "variant2") {
        return (
            <Box sx={{ mx: ["1.063rem", "1.063rem", "1.375rem", "auto"], ...customStyles }}>
                <Heading sx={{ ...typography.h2, textAlign: "center", p: { m: 0 }, mb: "1.5rem", mx: "2rem" }} dangerouslySetInnerHTML={{ __html: heading }} />
                <Box sx={{ maxWidth: ["100%", "100%", "100%", "60%"], margin: "0 auto", textAlign: "center" }}>
                    <Text sx={{ ...typography.textMd400, textAlign: "center", p: { m: 0 } }} dangerouslySetInnerHTML={{ __html: description }} />
                </Box>
                <Flex sx={mainTabContainerStyles}>
                        {showArrows && (
                          <>
                            <Flex sx={{ ...tabLeftContainer, ...arrowStyles }} onClick={handleLeftArrowClick}>
                             <Icon name="ChevronLeft" size={32} color={colors.primaryMain} />
                            </Flex>
                            <Flex sx={{ ...tabRightContainer, ...arrowStyles }} onClick={handleRightArrowClick}>
                             <Icon name="ChevronRight" size={32} color={colors.primaryMain} />
                            </Flex>
                          </>
                        )}
                    <Flex
                        ref={tabsContainerRef}
                        sx={tabContainerStyles}
                    >
                        {personas?.map((persona, index) => (
                            <Box
                                key={persona.title}
                                sx={{
                                    ...personaTabStyles,
                                    ...(persona.image?.gatsbyImageData
                                        ? { width: '9.063rem' }
                                        : { minWidth: '9.063rem', px: '1rem' }),
                                    border: `2px solid ${selectedTab === index ? colors.primaryMain : colors.neutral200}`,
                                }}
                                onClick={() => handleTabClick(index)}
                            >
                                {selectedTab === index && (
                                    <Box
                                        sx={{
                                            ...selectedTabStyles,
                                            animation: `${progressAnimation} ${isSelectedTab ? '55s' : '20s'} linear infinite`,
                                        }}
                                    />
                                )}
                                {persona.image?.gatsbyImageData && (
                                    <GatsbyImage
                                        image={getImage(persona.image.gatsbyImageData)}
                                        alt={persona.image.alt}
                                        sx={imageStylesV1}
                                    />
                                )}
                                {persona.title && (
                                    <Heading className="persona-title" sx={{ ...typography.h6, ...(persona.image?.gatsbyImageData && { mt: '1rem' }), position: 'relative', zIndex: 0, color: selectedTab === index ? colors.primary700 : colors.neutral900, }}>
                                        {persona.title}
                                    </Heading>
                                )}
                            </Box>
                        ))}
                    </Flex>
                </Flex>
                {selectedTab !== null && (
                    <Box sx={{ mt: ["3rem", "3rem", "2rem", "2rem"] }}>
                        <Flex sx={desktopSectionStylesV2}>
                            {personas[selectedTab]?.sections?.map((section, index) => (
                                <Box
                                    key={`desktop-section-${index}`}
                                    sx={{
                                        ...featureDescriptionV2,
                                        backgroundColor: colors[`${section?.color}100`],
                                        ...(index === 0 && {
                                            ...featureSectionLeftV2,
                                            ...(contentSectionWidth && {
                                                "@media (max-width: 1024px)": {
                                                    ...featureSectionLeftV2["@media (max-width: 1024px)"],
                                                    width: contentSectionWidth,
                                                },
                                            }),
                                        }),
                                        ...(index === 1 && {
                                            ...imageSectionCustomStyles,
                                            ...featureSectionRightV2
                                        }),
                                    }}
                                >
                                    {index === 0 ? (
                                        <Box>
                                            <Heading
                                                sx={{ ...typography.h5, mt: "1.5rem", color: colors.neutral900 }}
                                                dangerouslySetInnerHTML={{ __html: section?.heading }}
                                            />
                                            <Text
                                                sx={listStyles(section?.bulletColor)}
                                                dangerouslySetInnerHTML={{ __html: section?.description }}
                                            />
                                        </Box>
                                    ) : index === 1 && section?.image ? (
                                        <Flex
                                            sx={{
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: "100%",
                                                width: "100%",
                                            }}
                                        >
                                            <Box sx={{ ...imageSectionStylesV2, ...imageCustomStyles }}>
                                                <GatsbyImage
                                                    image={getImage(section.image.gatsbyImageData)}
                                                    alt={section?.heading}
                                                    objectFit="contain"
                                                    style={{ objectFit: "contain" }}
                                                    imgStyle={{ objectFit: "contain" }}
                                                />
                                            </Box>
                                        </Flex>
                                    ) : null}
                                </Box>
                            ))}
                        </Flex>
                    </Box>
                )}
                <Flex sx={{ justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                    {selectedTab !== null && personas[selectedTab] && (
                        (personas[selectedTab].primaryCtaName || personas[selectedTab].secondaryCtaName) && (
                            <Flex sx={ctaContainerStyles}>
                                {personas[selectedTab].primaryCtaName && personas[selectedTab].primaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="filled"
                                        id={`${pageName.toLowerCase()}_persona_variant2_primary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() => openPrimaryCta(personas[selectedTab].primaryCtaUrl)}
                                        customStyles={{ width: "max-content" }}
                                    >
                                        {personas[selectedTab].primaryCtaName}
                                    </Button>
                                )}
                                {personas[selectedTab].secondaryCtaName && personas[selectedTab].secondaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="outlined"
                                        id={`${pageName.toLowerCase()}_persona_variant2_secondary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() =>
                                            window.open( personas[selectedTab].secondaryCtaUrl, "_blank", "noopener,noreferrer" )
                                        }
                                        customStyles={{ width: "max-content" }}
                                    >
                                        {personas[selectedTab].secondaryCtaName}
                                    </Button>
                                )}
                            </Flex>
                        )
                    )}
                </Flex>
            </Box>
        );
    }
};

const listStyles = (bulletColor) => ({
    ...typography.textMd400,
    color: colors.neutral900,
    "p": {
        ...typography.textMd400,
        color: colors.neutral900,
        py: "0.75rem"
    },
    "h5": {
        ...typography.h5,
        color: colors.neutral900,
    },
    "h6": {
        ...typography.h6,
        display: "inline",
        color: colors.neutral900,
    },
    "& ul": {
        listStyle: "none",
        pl: "0",
        ml: "0",
    },
    "& li": {
        position: "relative",
        mb: ["0.75rem", "0.75rem", "1rem", "1rem"],
        pl: "1.5rem",
        "::before": {
            content: '""',
            display: "inline-block",
            width: "0.875rem",
            height: "0.875rem",
            backgroundColor: colors[`${bulletColor}500`],
            borderRadius: "50%",
            position: "absolute",
            left: "0",
            top: "0.4rem",
        },
    },
});

const {
    featureDescriptionV1,
    imageStylesV1,
    desktopSectionStylesV1,
    featureDescriptionV2,
    personaTabStyles,
    desktopSectionStylesV2,
    enableStatsLayoutV1,
    featureSectionLeftV2,
    featureSectionRightV2,
    imageSectionStylesV2
} = {
    featureDescriptionV1: {
        borderRadius: "0.75rem",
        px: "2rem",
        flex: "1",
        flexDirection: "column",
        maxWidth: "34.875rem",
        height: "100%",
    },
    imageStylesV1: {
        position: 'relative',
        zIndex: 1,
        width: '80%',
        height: 'auto',
    },
    desktopSectionStylesV1: {
        justifyContent: "space-between",
        alignItems: "stretch",
        "@media (max-width: 1024px)": {
            flexDirection: "column",
            alignItems: "center",
        },
        gap: [0, 0, 0, "2rem"]
    },
    featureDescriptionV2: {
        borderRadius: "0.75rem",
        px: "2rem",
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    personaTabStyles: {
        flexShrink: 0,
        paddingY: '1.063rem',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        scrollSnapAlign: 'center',
        borderRadius: '0.5rem',
        boxSizing: 'border-box',
        opacity: 1,
        cursor: 'pointer',
        "@media (min-width: 1025px)": {
            "&:hover": {
                borderColor: colors.primaryMain,
            },
            "&:hover .persona-title": {
                color: colors.primary700,
            },
        },
    },
    desktopSectionStylesV2: {
        justifyContent: "center",
        alignItems: "stretch",
        mx: "2rem",
        gap: "1.5rem",
        "@media (max-width: 1024px)": {
            flexDirection: "column-reverse",
            alignItems: "stretch",
            mx: 0,
            width: "100%",
        },
    },
    enableStatsLayoutV1: {
    ".stats": {
      py: "1.5rem",
      display: "flex",
      flexWrap: ["wrap", "wrap", "nowrap", "nowrap"],
      gap: "1.5rem",
    },
    ".stats > div": {
      borderLeft: `4px solid ${colors.secondaryGreenMain}`,
      pl: 3,
      width: ["calc(50% - 0.75rem)", "calc(50% - 0.75rem)", "auto", "auto"],
    },
    ".stats h2": {
      ...typography.h2,
      color: colors.primary700,
      margin: 0,
    },
    ".stats p": {
      ...typography.textSm,
      color: colors.neutral900,
      marginTop: 2,
    },
  },
  featureSectionLeftV2: {
    border: `1px solid ${colors.neutral200}`,
    maxWidth: "46.313rem",
    "@media (max-width: 1024px)": {
      width: "30rem",
      maxWidth: "100%",
      flexGrow: 0,
      alignSelf: "center",
      boxSizing: "border-box",
      pb: "1.5rem",
    },
  },
  featureSectionRightV2: {
    maxWidth: "26.125rem",
    "@media (max-width: 1024px)": {
      width: "30rem",
      maxWidth: "100%",
      flexGrow: 0,
      alignSelf: "center",
      boxSizing: "border-box",
    },
  },
   imageSectionStylesV2: {
    pt: "1.5rem",
    "@media (max-width: 1024px)": {
      width: "100%",
      maxWidth: "100%",
      "& .gatsby-image-wrapper": {
        width: "100%",
        maxWidth: "100%",
      },
      "& .gatsby-image-wrapper > div:first-of-type": {
        width: "100%",
        maxWidth: "100%",
      },
      "& .gatsby-image-wrapper > div:first-of-type img": {
        width: "100%",
        height: "auto",
        maxWidth: "100%",
        maxHeight: "none",
      },
      "& .gatsby-image-wrapper [data-main-image]": {
        objectFit: "contain",
      },
    },
  },
};

const {
    mainTabContainerStyles,
    ctaContainerStyles,
    tabLeftContainer,
    tabRightContainer,
    tabContainerStyles,
    selectedTabStyles
} = {
    mainTabContainerStyles: {
        position: "relative",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
    },
    ctaContainerStyles: {
        mt: ["3rem", "3rem", "3rem", "4rem"],
        alignItems: "center",
        flexDirection: ["column", "column", "row"],
        gap: ["1.5rem", "1.5rem", "3rem", "3rem"],
    },
    tabLeftContainer: {
        width: "2rem",
        position: "absolute",
        left: 0,
        zIndex: 1,
        justifyContent: "center",
        alignItems: "center",
        background:
            "linear-gradient(to right, #ffffff, #ffffff, #ffffff, #ffffff)",
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            ml: "1.45rem",
        },
    },
    tabRightContainer: {
        width: "2rem",
        position: "absolute",
        right: 0,
        zIndex: 1,
        justifyContent: "center",
        alignItems: "center",
        background:
            "linear-gradient(to left, #ffffff, #ffffff, #ffffff, #ffffff)",
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            mr: "1.45rem",
        },
    },
    tabContainerStyles: {
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        padding: '1rem 0 1rem 0',
        marginTop: '3rem',
        gap: '2rem',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        justifyContent: 'flex-start',
        alignItems: 'center',
        mx: "auto",
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            maxWidth: "52.625rem"
        },
    },
    selectedTabStyles: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        backgroundColor: colors.primary100,
        zIndex: 0,
        inset: 0,
        borderRadius: '0.5rem',
        boxSizing: 'border-box',
    },
}

export default PersonaSection;